import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Activity, Task } from "../../types/tasks";
import { TaskStatusBadge } from "./TaskStatusBadge";

type GanttRange = "day" | "week" | "month";

interface Column {
  key: string;
  label: string;
  sublabel: string;
  isToday: boolean;
  /** Minimum column width (px). Columns flex-grow to fill remaining space. */
  minW: number;
}

interface GanttRow {
  task: Task;
  bySlot: Record<string, Activity[]>;
}

interface Props {
  tasks: Task[];
  isAdmin: boolean;
  onEditTask: (task: Task) => void;
}

/** Returns the border-l color class for the activity card stripe. */
function activityCardStripe(a: Activity): string {
  if (a.completedAt) return "border-l-gray-400";
  if (!a.dueDate) return "border-l-gray-300";
  const today = format(new Date(), "yyyy-MM-dd");
  if (a.dueDate < today) return "border-l-blue-600";   // overdue → blue stripe
  if (a.dueDate === today) return "border-l-amber-500"; // today → amber stripe
  return "border-l-blue-400";                           // upcoming → blue stripe
}

function parseHour(dueTime?: string): number | null {
  if (!dueTime) return null;
  const h = parseInt(dueTime.split(":")[0], 10);
  return isNaN(h) || h < 0 || h > 23 ? null : h;
}

// All 24 hours for day view
const ALL_HOURS = Array.from({ length: 24 }, (_, i) => i);

const TASK_COL_W = 220; // fixed, never flex-grows
const MONTH_MIN_W = 34;
const WEEK_MIN_W = 100;
const ALLDAY_MIN_W = 64;
const HOUR_MIN_W = 40;

export const TaskGantt: React.FC<Props> = ({ tasks, isAdmin, onEditTask }) => {
  const { t } = useTranslation("tasks");
  const [range, setRange] = useState<GanttRange>("month");
  const [currentDate, setCurrentDate] = useState(() => new Date());

  const navigate = (dir: -1 | 1) =>
    setCurrentDate((d) => {
      if (range === "month") return addMonths(d, dir);
      if (range === "week") return addWeeks(d, dir);
      return addDays(d, dir);
    });

  const goToday = () => setCurrentDate(new Date());

  const { columns, rows, headerLabel } = useMemo((): {
    columns: Column[];
    rows: GanttRow[];
    headerLabel: string;
  } => {
    // ── MONTH ──────────────────────────────────────────────────────────────
    if (range === "month") {
      const mStart = startOfMonth(currentDate);
      const mEnd = endOfMonth(currentDate);
      const mStartStr = format(mStart, "yyyy-MM-dd");
      const mEndStr = format(mEnd, "yyyy-MM-dd");
      const days = eachDayOfInterval({ start: mStart, end: mEnd });

      const cols: Column[] = days.map((d) => ({
        key: format(d, "yyyy-MM-dd"),
        label: format(d, "d"),
        sublabel: format(d, "EEE").charAt(0),
        isToday: isToday(d),
        minW: MONTH_MIN_W,
      }));

      const ganttRows: GanttRow[] = tasks
        .map((task) => {
          const bySlot: Record<string, Activity[]> = {};
          task.activities.forEach((a) => {
            if (!a.dueDate || a.dueDate < mStartStr || a.dueDate > mEndStr) return;
            if (!bySlot[a.dueDate]) bySlot[a.dueDate] = [];
            bySlot[a.dueDate].push(a);
          });
          return { task, bySlot };
        })
        .filter(({ bySlot }) => Object.keys(bySlot).length > 0);

      return { columns: cols, rows: ganttRows, headerLabel: format(currentDate, "MMMM yyyy") };
    }

    // ── WEEK ───────────────────────────────────────────────────────────────
    if (range === "week") {
      const wStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const wEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      const wStartStr = format(wStart, "yyyy-MM-dd");
      const wEndStr = format(wEnd, "yyyy-MM-dd");
      const days = eachDayOfInterval({ start: wStart, end: wEnd });

      const cols: Column[] = days.map((d) => ({
        key: format(d, "yyyy-MM-dd"),
        label: format(d, "EEE"),
        sublabel: format(d, "d MMM"),
        isToday: isToday(d),
        minW: WEEK_MIN_W,
      }));

      const ganttRows: GanttRow[] = tasks
        .map((task) => {
          const bySlot: Record<string, Activity[]> = {};
          task.activities.forEach((a) => {
            if (!a.dueDate || a.dueDate < wStartStr || a.dueDate > wEndStr) return;
            if (!bySlot[a.dueDate]) bySlot[a.dueDate] = [];
            bySlot[a.dueDate].push(a);
          });
          return { task, bySlot };
        })
        .filter(({ bySlot }) => Object.keys(bySlot).length > 0);

      return {
        columns: cols,
        rows: ganttRows,
        headerLabel: `${format(wStart, "d MMM")} – ${format(wEnd, "d MMM yyyy")}`,
      };
    }

    // ── DAY (all 24 hours) ─────────────────────────────────────────────────
    const dayStr = format(currentDate, "yyyy-MM-dd");

    const cols: Column[] = [
      { key: "allday", label: t("gantt.allDay"), sublabel: "", isToday: false, minW: ALLDAY_MIN_W },
      ...ALL_HOURS.map((h) => ({
        key: `h${h}`,
        label: String(h).padStart(2, "0"),
        sublabel: ":00",
        isToday: false,
        minW: HOUR_MIN_W,
      })),
    ];

    const ganttRows: GanttRow[] = tasks
      .map((task) => {
        const bySlot: Record<string, Activity[]> = {};
        task.activities.forEach((a) => {
          if (a.dueDate !== dayStr) return;
          const h = parseHour(a.dueTime);
          const slotKey = h !== null ? `h${h}` : "allday";
          if (!bySlot[slotKey]) bySlot[slotKey] = [];
          bySlot[slotKey].push(a);
        });
        return { task, bySlot };
      })
      .filter(({ bySlot }) => Object.keys(bySlot).length > 0);

    return {
      columns: cols,
      rows: ganttRows,
      headerLabel: format(currentDate, "EEEE, d MMMM yyyy"),
    };
  }, [range, currentDate, tasks, t]);

  /**
   * Minimum grid width drives the horizontal scroll threshold.
   * Column cells use `flex: 1 0 minW` so they grow proportionally beyond this
   * minimum — which fills all remaining space and eliminates the trailing gap.
   */
  const minGridW = TASK_COL_W + columns.reduce((s, c) => s + c.minW, 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Navigation & range switcher */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          <h2 className="text-sm font-semibold text-gray-900 text-center min-w-[160px]">
            {headerLabel}
          </h2>
          <button
            onClick={() => navigate(1)}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-gray-300 bg-white overflow-hidden text-xs">
            {(["day", "week", "month"] as GanttRange[]).map((r, idx) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  idx > 0 ? "border-l border-gray-300" : ""
                } ${
                  range === r
                    ? "bg-primary text-white"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {t(`gantt.range.${r}`)}
              </button>
            ))}
          </div>
          <button
            onClick={goToday}
            className="text-xs px-3 py-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {t("calendar.today")}
          </button>
        </div>
      </div>

      {/* Scrollable chart */}
      <div className="overflow-x-auto">
        <div style={{ width: "100%", minWidth: `${minGridW}px` }}>
          {/* Header row */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            <div
              style={{ width: TASK_COL_W, minWidth: TASK_COL_W }}
              className="flex-shrink-0 px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-gray-200"
            >
              {t("gantt.taskColumn")}
            </div>
            {columns.map((col) => (
              <div
                key={col.key}
                style={{ flex: `1 0 ${col.minW}px` }}
                className={`border-r border-gray-100 text-center py-1.5 ${
                  col.isToday ? "bg-primary/10" : ""
                }`}
              >
                <div
                  className={`text-xs font-semibold leading-tight ${
                    col.isToday ? "text-primary" : "text-gray-700"
                  }`}
                >
                  {col.label}
                </div>
                {col.sublabel && (
                  <div className="text-[10px] text-gray-400 leading-tight">
                    {col.sublabel}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Empty state */}
          {rows.length === 0 && (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
              {t("gantt.empty")}
            </div>
          )}

          {/* Task rows */}
          {rows.map(({ task, bySlot }) => (
            <div
              key={task._id}
              className="flex border-b border-gray-100 hover:bg-gray-50/40 group"
            >
              {/* Left: task info (fixed width) */}
              <div
                style={{ width: TASK_COL_W, minWidth: TASK_COL_W }}
                className="flex-shrink-0 px-3 py-2 border-r border-gray-200 flex flex-col justify-center gap-1 cursor-pointer"
                onClick={() => onEditTask(task)}
              >
                <div
                  className="text-xs font-medium text-gray-900 truncate leading-snug"
                  title={task.description}
                >
                  {task.taskNumber && (
                    <span className="text-gray-400 font-normal mr-1">{task.taskNumber}</span>
                  )}
                  {task.description}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <TaskStatusBadge status={task.status} />
                  {isAdmin && task.assigneeName && (
                    <span className="text-[11px] text-gray-400 truncate max-w-[90px]">
                      {task.assigneeName}
                    </span>
                  )}
                </div>
              </div>

              {/* Right: slot cells (flex-grow, same flex ratio as header) */}
              {columns.map((col) => {
                const slotActivities = bySlot[col.key] ?? [];
                return (
                  <div
                    key={col.key}
                    style={{ flex: `1 0 ${col.minW}px` }}
                    className={`border-r border-gray-100 flex flex-col gap-1 p-1 justify-start min-h-[44px] overflow-hidden min-w-0 ${
                      col.isToday ? "bg-primary/5" : ""
                    }`}
                  >
                    {/*
                      Multiple activities on the same day/hour slot each render
                      as their own bordered card on a separate row.
                    */}
                    {slotActivities.map((activity) => {
                      const stripe = activityCardStripe(activity);
                      return (
                        <button
                          key={activity.id}
                          onClick={() => onEditTask(task)}
                          className={`w-full min-w-0 text-left rounded-sm border border-gray-200 border-l-4 ${stripe} bg-white px-1.5 py-1 hover:shadow-sm transition-shadow overflow-hidden`}
                        >
                          <div className="text-xs font-semibold text-gray-800 leading-tight truncate">
                            {activity.title}
                          </div>
                          {task.assigneeName && range !== "month" && (
                            <div className="text-[10px] text-gray-500 leading-none mt-0.5 truncate">
                              {task.assigneeName}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 px-4 py-2.5 border-t border-gray-100 bg-gray-50">
        {(
          [
            { key: "upcoming", color: "bg-blue-400" },
            { key: "today", color: "bg-amber-500" },
            { key: "overdue", color: "bg-blue-600" },
            { key: "done", color: "bg-gray-400" },
          ] as const
        ).map(({ key, color }) => (
          <span key={key} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className={`w-2 h-2 rounded-full inline-block ${color}`} />
            {t(`calendar.legend.${key}`)}
          </span>
        ))}
        <span className="ml-auto text-xs text-gray-400 italic">{t("gantt.hint")}</span>
      </div>
    </div>
  );
};
